/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/

import { every } from 'lodash';
import { DateTime } from 'luxon';
import * as Yup from 'yup';

import { KubernetesIdentifier } from 'shared';

import { DATASET_DEFAULT_READONLY } from '../../constants/settings';
import { ImportDatasetMeta } from '../../types/miscellaneous/ImportDatasetMeta';
import { getSuggestedTimeString } from '../../utils/getSuggestedTimeForSchedulling';
import { isValidSubnetMask } from '../../utils/validators';
import { CreateClusterFromBackupValues, CreateClusterValues } from './CreateClusterOverlay.types';

export const formInitialValues: CreateClusterValues = {
  name: '',
  description: '',
  organizationId: '',
  projectId: '',
  supportPlanId: '',
  replicas: 0,
  allowAllConnections: false,
  allowedSourceRanges: [],
  allowedSourceRangesInput: '',
  backupConfigurationLogicalEnabled: false,
  backupConfigurationLogicalSchedule: `${getSuggestedTimeString().randomMinute} ${
    getSuggestedTimeString().randomHour
  } * * *`,
  backupConfigurationLogicalDaysToRetain: '5',
  backupConfigurationContinuousEnabled: true,
  backupConfigurationContinuousSchedule: `${getSuggestedTimeString().randomMinute} ${
    getSuggestedTimeString().randomHour
  } * * *`,
  backupConfigurationContinuousDaysToRetain: '5',
  selectedDataSetCategoryId: undefined,
  selectedDataSetIds: [],
  dataSetsMetas: {},
  cpuUnits: 1,
  memoryGbs: 1,
  storageGbs: 1,
  postgresVersion: 13,
  storageClassId: '',
  maintenanceWindows: [
    {
      weekday: '',
      startTimeUtc: DateTime.fromObject({ hour: 0, minute: 0 }, { zone: 'utc' }),
      endTimeUtc: DateTime.fromObject({ hour: 5, minute: 0 }, { zone: 'utc' }),
    },
  ],
};

export const formFromBackupInitialValues: CreateClusterFromBackupValues = {
  ...formInitialValues,
  backupOrganizationId: undefined,
  backupProjectId: undefined,
  backupClusterId: undefined,
  backupCluster: null,
  backupTimestampMinDate: new Date('1900-01-01'),
  backupTimestampMaxDate: new Date('2099-12-31'),
  backupTimestamp: new Date(),
  cloneAcl: false,
  cloneParameters: false,
  cloneLogicalBackupConfiguration: false,
  cloneContinuousBackupConfiguration: false,
  oldestRestorePoint: null,
};

export const createClusterInfoValidationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .min(KubernetesIdentifier.MinLength, 'min_length')
    .max(KubernetesIdentifier.MaxLength, 'max_length')
    .matches(KubernetesIdentifier.Regex, 'invalid')
    .required('empty'),
  description: Yup.string().trim().max(200, 'max').nullable(),
  organizationId: Yup.string(),
  projectId: Yup.string().required('create-cluster:validation.project.required').notOneOf([null], 'required'),
  supportPlanId: Yup.string(),
  allowAllConnections: Yup.boolean().required(),
  allowedSourceRangesInput: Yup.string().test('is valid network mask', 'not_valid', function (value) {
    return !value || isValidSubnetMask(value);
  }),
  allowedSourceRanges: Yup.array()
    .of(Yup.string())
    .test('is array of net masks', 'not_valid', function (value) {
      const { allowAllConnections } = this.parent;
      const isValid = every(value, isValidSubnetMask);
      if (isValid) {
        return true;
      }
      return allowAllConnections;
    }),
});

export const createClusterBackupStepValidationSchema = Yup.object({
  backupOrganizationId: Yup.string(),
  backupProjectId: Yup.string(),
  backupClusterId: Yup.string(),
  backupCluster: Yup.mixed().required('empty').notOneOf([null], 'empty'),
  backupClusterConfiguration: Yup.mixed().notOneOf([null]),
  backupTimestampMinDate: Yup.date(),
  backupTimestampMaxDate: Yup.date(),
  backupTimestamp: Yup.date()
    .nullable()
    .notOneOf([undefined], 'invalid_date')
    .required('empty')
    .min(Yup.ref('backupTimestampMinDate'), 'before_min_date')
    .max(Yup.ref('backupTimestampMaxDate'), 'after_max_date'),
  cloneAcl: Yup.boolean(),
  cloneParameters: Yup.boolean(),
  cloneLogicalBackupConfiguration: Yup.boolean(),
  cloneContinuousBackupConfiguration: Yup.boolean(),
});

export const createClusterBackupConfigureStepValidationSchema = Yup.object({
  backupConfigurationLogicalEnabled: Yup.boolean(),
  backupConfigurationLogicalSchedule: Yup.string(),
  backupConfigurationLogicalDaysToRetain: Yup.string()
    .test('day to retain', 'not_valid', value => !/^\d*\.{1}\d*$/.test(value) && value > 0 && value <= 60)
    .required('required'),
  backupConfigurationContinuousEnabled: Yup.boolean(),
  backupConfigurationContinuousSchedule: Yup.string(),
  backupConfigurationContinuousDaysToRetain: Yup.string()
    .test('day to retain', 'not_valid', value => !/^\d*\.{1}\d*$/.test(value) && value > 1 && value <= 60)
    .required('required'),
});

export const createClusterMaintenanceWindowsConfigureStepValidationSchema = Yup.object({
  maintenanceWindows: Yup.array()
    .of(
      Yup.object({
        weekday: Yup.string(),
        startTimeUtc: Yup.mixed()
          .required('empty')
          .test('is luxon datetime', 'invalid_time', value => DateTime.isDateTime(value) && value.isValid),
        endTimeUtc: Yup.mixed()
          .required('empty')
          .test('is luxon datetime', 'invalid_time', value => DateTime.isDateTime(value) && value.isValid)
          .test('later than start', 'later_than', function (value) {
            const { startTimeUtc } = this.parent;

            if (!DateTime.isDateTime(value) || !value.isValid) return true;
            if (!DateTime.isDateTime(startTimeUtc) || !startTimeUtc.isValid) return true;

            return value > startTimeUtc;
          }),
      }),
    )
    .max(6, 'max_length')
    .test('daily exclusive', 'maintenance_windows_daily_exclusive', value => {
      if (!Array.isArray(value) || value.length === 0) return true;

      const hasDaily = value.some(item => !item?.weekday);
      const hasMultiple = value.length > 1;

      return !(hasDaily && hasMultiple);
    }),
});

export const WeekdayMap = {
  '': 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};

export const initialImportDatasetMeta: ImportDatasetMeta = {
  readonly: DATASET_DEFAULT_READONLY,
};
